import { CurrencyDisplay } from '@lib/common';
import { RoomType } from '@lib/state';
import { OccupancyType } from '@lib/state/api/generated/properties/models/occupancy-type';
import { RatePlanProperty, RoomRatePlan } from 'app/state';
import React from 'react';
import { NumberCellInput } from './number-cell-input';

export const supportsDoubleOccupancy = (roomType?: RoomType) =>
  roomType?.occupancyType === OccupancyType.Double;

/** Renders a read-only DSO Fee column; omitted when none of the rate plans are double occupancy. */
export function DSOFeeColumn(ratePlanRoomTypes: RoomType[]) {
  if (!ratePlanRoomTypes.some(supportsDoubleOccupancy)) {
    return [];
  }

  return [
    {
      title: 'DSO Fee',
      align: 'center' as const,
      colSpan: 1.2,
      valueFactory: (plan: RoomRatePlan) =>
        !plan.dsoFee || plan.dsoFee === 0 ? (
          '--'
        ) : (
          <CurrencyDisplay value={plan.dsoFee} key={plan.id} />
        ),
    },
  ];
}

/**
 * Editable DSO Fee column for the per-property overrides on Create. The caller decides whether the
 * column is shown (the selected room type's occupancy); a row whose own room type is not double
 * occupancy shows '--' instead of an input, so no DSO Fee is entered for it.
 */
export function DSOFeeOverrideColumn(
  isRowEditable: (plan: RoomRatePlan) => boolean,
  getValue: (plan: RoomRatePlan) => number,
  onChange: (plan: RoomRatePlan, value: number | undefined) => void
) {
  return [
    {
      title: 'DSO Fee',
      align: 'center' as const,
      colSpan: 1.2,
      valueFactory: (plan: RoomRatePlan) =>
        isRowEditable(plan) ? (
          <NumberCellInput
            value={getValue(plan)}
            onChange={value => onChange(plan, value)}
            error={getValue(plan) < 0}
            helperText={getValue(plan) < 0 ? 'Value cannot be less than zero' : undefined}
            aria-label={`DSO fee for ${(plan as RatePlanProperty).propertyName}`}
            data-testid="ratePlanRowDsoFeeInput"
          />
        ) : (
          '--'
        ),
    },
  ];
}
